'use client'
import React, { useEffect, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Check, X } from 'lucide-react';
import PlanFeatures from '@/data/planFeatures.json';
import { getPlansQuota, getUserPlan, isFreePlan, upgradePlan } from './actions';
import HttpStatus from '@/lib/HttpStatus';
import LoadingGif from '@/../public/loading.gif';
import Image from 'next/image';
import StripeLogo from '@/../public/images/Stripe_Logo_1.png';
import PlansSkeletons from './plansSkeletons';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';

interface IPlans {
  MonthlyTokens: number;
  DIYSaves: number;
  ArticlesSaves: number;
  EmailDraftSaves: number;
  ResumeSaves: number;
  MarketDetectiveSaves: number;
  InterviewQuestionsSaves: number;
  SuccessStorySaves: number;
  planName: string;
  id?: string | undefined;
  ArticlesPublish?: boolean | null | undefined;
  ArticleImageHeaderGenerator?: boolean | null | undefined;
  planMonthlyPrice?: string | null | undefined;
  whatsappCapping?: number | undefined;
  priceId: string;
}

const PlanBullets = (plan: IPlans) => {
  return (
    <>
      <div className='font-bold'>
        Includes:
      </div>
      <ul className="my-2 md:ml-2 mb-6 list-none [&>li]:mt-2 text-xs lg:text-sm">
        {
          Object.entries(plan).map(([key, value]) => {
            if(key != 'id' && key != 'planMonthlyPrice' && key != 'priceId') {
              return (
                <li key={key!}>
                  <div className='flex flex-row justify-start'>
                    <div className='mr-2 flex flex-row items-center'>
                      {
                        value == false || value == "false" ?
                        <X className='text-red-500' /> :
                        <Check className='text-green-500' />
                      }
                    </div>
                    <div className='flex flex-row items-center'>
                      <span className='font-medium mr-3'>{`${value !== false && value !== "false" && value !== true && value !== "true" ? value : ''} ${PlanFeatures[key as keyof typeof PlanFeatures]}`}</span>
                    </div>
                  </div>
                </li>
              )
            }
          })
        }
      </ul>
    </>
  )
}

function Plans() {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [ plans, setPlans ] = useState<IPlans[]>();
  const [ isFetching, setIsFetching ] = useState<boolean>(true);
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [ userPlan, setUserPlan ] = useState<string | null>(null);
  const [ isFreeUser, setIsFreeUser ] = useState<boolean>(false);

  useEffect(() => {
    async function getPLans() {
      setIsFetching(true);
      const results = await getPlansQuota();

      if(results.status == HttpStatus.OK) {
        setPlans(results.data);
      } else {
        setPlans([]);
      }

      if(user && user.id) {
        const userPlanRes = await getUserPlan(user!.id);
        if(userPlanRes.status == HttpStatus.OK) {
          if(userPlanRes.data.length > 0)
            setUserPlan(userPlanRes.data[0].userPlan);
          else 
            setUserPlan(null);
        }

        const freePlan = await isFreePlan(user!.id);
        if(freePlan.status == HttpStatus.OK) {
          setIsFreeUser(freePlan.data);
        } else setIsFreeUser(false);
      }

      setIsFetching(false);
    }

    getPLans();

  }, []);

  useEffect(() => {
    async function getUserPlanDetails() {
      if(user && user.id) {
        const userPlanRes = await getUserPlan(user!.id);
        if(userPlanRes.status == HttpStatus.OK) {
          if(userPlanRes.data.length > 0)
            setUserPlan(userPlanRes.data[0].userPlan);
          else 
            setUserPlan(null);
        }

        const freePlan = await isFreePlan(user!.id);
        if(freePlan.status == HttpStatus.OK) {
          setIsFreeUser(freePlan.data);
        } else setIsFreeUser(false);
      }

    }
    
    if(user && !isLoading)
      getUserPlanDetails();

  }, [user, isLoading, isAuthenticated]);

  const handleUpgrade = async (priceId: string, planId: string) => {
    if (!priceId) return; // Handle free plan logic elsewhere

    if(isFreeUser) {
      // This is a free user, do a new checkout
      setLoadingId(priceId);
      try {
        const response = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ priceId, planId }),
        });
          
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url; // Redirect to Stripe
        }
      } catch (error) {
        console.error("Payment Error:", error);
      } finally {
        setLoadingId(null);
      }
    } else {
      // The user is already on a paid plan. Just do an upgrade of subscription...

      try {
        const upgradPlanRes = await upgradePlan(user?.id!, priceId);

      } catch(error) {
        console.error("Upgrade Error:", error);
      }
    }
    
  };
  
  return (
    <div className='h-auto pb-10 w-full md:w-11/12 lg:w-4/5 flex flex-col justify-start items-start'>
      <div className='w-full flex justify-center items-center'>
        <AdSterraNativeBanner />
      </div>
      
      <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Available Plans
      </h1>
      {
        isFetching 
        // ? <Image src={LoadingGif} alt={'Loading Data'} width={40} /> 
        ? <PlansSkeletons />
        : null
      }
      <div className="w-full mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {
          plans && plans.length > 0 && !isFetching 
          ? (
              plans!.map((plan) => (
                <Card key={plan.id} className={"shadow-md hover:shadow-lg transition-all duration-200 dark:bg-gray-800 animate-in fade-in duration-1000 slide-in-from-bottom-2"
                    + (plan.planName !== 'Starter' ? ' bg-yellow-300' : '')}>
                  <CardHeader>
                    <CardTitle className="text-3xl font-bold truncate
                      flex flex-row justify-center items-center">
                      {plan.planName !== 'Starter' ? 
                        <DynamicIcon name="gem"
                          className="w-7 h-7 mr-3 text-white drop-shadow-[0_3px_2px_#965205]" />
                        : null
                      }
                      <span className={plan.planName !== 'Starter' ? '[text-shadow:1px_1px_0_#b86507,2px_2px_0_#b86507,3px_3px_0_#b86507,4px_4px_10px_rgba(0,0,0,0.5)] text-3xl text-white' : 'text-indigo-600'}>
                        {plan.planName}
                      </span>
                    </CardTitle>
                    {/* <p className="text-sm text-gray-500 dark:text-gray-400">{draft.date}</p> */}
                  </CardHeader>
                  <CardContent>
                    {/* <p className="text-gray-700 dark:text-gray-300 text-sm italic"> */}
                      {PlanBullets(plan)}
                    {/* </p> */}
                    <p className="text-black font-bold dark:text-gray-300 text-sm italic">
                      {
                        plan.planMonthlyPrice && parseFloat(plan.planMonthlyPrice) == 0 ? 'Free' :
                        `$${plan.planMonthlyPrice} / Month`
                      }
                    </p>
                  </CardContent>
                  <CardFooter className='p-0'>
                      <div 
                        className={`bg-indigo-400 p-2 min-h-[80px] w-full rounded-bl-md rounded-br-md 
                          flex flex-col justify-center text-center italic ${plan.id != userPlan ? 'cursor-pointer' : ''}`}
                        onClick={
                          plan.id != userPlan 
                          ? () => handleUpgrade(plan.priceId!, plan.id!) 
                          : () => null
                        }
                      >
                        {
                          plan.id == userPlan 
                          ? (<div>
                              <span className='text-white text-xl font-medium'>Your Current Plan</span>
                            </div>)
                          : (<div className='flex flex-col justify-center items-center'>
                              <div>
                                <span className='text-white text-xl font-medium'>Proceed to Checkout</span>
                              </div>
                              <div className='flex flex-row justify-center items-center'>
                                <span className='text-sm text-white'>via </span>
                                <Image src={StripeLogo} width={70} alt="Stripe Logo" />
                              </div>
                            </div>)
                        }
                      </div>
                  </CardFooter>
                </Card>
              ))
            )
          : null
        }
      </div>

      <div className='w-full flex justify-center items-center mt-8'>
        <AdSterraIFrameBannerAuto position='top' />
      </div>
      
    </div>
  )
}

export default Plans